/**
 * Translation Index
 * Central export for all translations
 * Non-English translations are lazy-loaded to reduce initial bundle size
 */

import { TranslationStrings, SupportedLanguage } from "../types";
import { en } from "./en";

// Lazy loaders for non-English translations
const lazyTranslations: Partial<Record<SupportedLanguage, () => Promise<{ default: TranslationStrings } | { fr: TranslationStrings } | { es: TranslationStrings } | { pt: TranslationStrings } | { ar: TranslationStrings } | { hi: TranslationStrings } | { zh: TranslationStrings }>>> = {
  fr: () => import("./fr"),
  es: () => import("./es"),
  pt: () => import("./pt"),
  ar: () => import("./ar"),
  hi: () => import("./hi"),
  zh: () => import("./zh"),
};

// Cache loaded translations
const cache: Partial<Record<SupportedLanguage, TranslationStrings>> = { en };

/**
 * Get translation strings for a language with fallback to English
 */
export async function getTranslationsAsync(language: SupportedLanguage): Promise<TranslationStrings> {
  if (cache[language]) return cache[language]!;
  const loader = lazyTranslations[language];
  if (!loader) return en;
  try {
    const mod = await loader() as Record<string, TranslationStrings>;
    const translations = mod[language] || mod.default || en;
    cache[language] = translations;
    return translations;
  } catch {
    return en;
  }
}

/**
 * Synchronous getter — returns cached or English fallback
 */
export function getTranslations(language: SupportedLanguage): TranslationStrings {
  return cache[language] || en;
}

/**
 * Preload a language's translations into cache
 */
export function preloadTranslation(language: SupportedLanguage): void {
  if (!cache[language]) {
    getTranslationsAsync(language);
  }
}
